import { useMemo, useState } from 'react';
import { AsYouType, CountryCode, validatePhoneNumberLength } from 'libphonenumber-js';
import { FlagIconCode } from 'ui/flags/flagIcons';
import { InputSelectCombo } from 'ui/inputSelectCombo';
import { InputSelectComboProps } from 'ui/inputSelectCombo/InputSelectCombo';
import { SelectValueDisplay, SelectValueDisplayProps } from 'ui/selects';
import { FlagIcon } from 'ui/flags';
import styled from 'styled-components';
import { spacing } from 'ui/theme';
import { COUNTRY_CALLING_CODES, DEFAULT_COUNTRY_OPTIONS } from './constants';
import { getCountryOptions, validateNumber } from './utils';

const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

type PhoneNumberCountryProps = {
  id: string;
  supportedCountries?: string[];
  countryValue?: string;
  phoneValue: string;
  placeholder?: string;
  withCountryFlag?: boolean;
  onPhoneChange: (phone: string) => void;
  onCountrySelect: (country: string) => void;
  onValidateError?: (e: Error) => void;
  validate?: boolean;
  validateErrorMessage?: string;
  hasError?: boolean;
};

const PhoneSelectTrigger = (withCountryFlag?: boolean) => (props: SelectValueDisplayProps) => {
  const countryValue = props.selectedOptions?.[0].value;
  const withLabelOverriden = {
    ...props.selectedOptions?.[0],
    label: (
      <Flex>
        {withCountryFlag && <FlagIcon code={countryValue as FlagIconCode} />}
        {countryValue}
      </Flex>
    ),
  };
  // @ts-ignore
  return <SelectValueDisplay {...props} selectedOptions={[withLabelOverriden]} />;
};

export const PhoneNumberCountry = ({
  id,
  supportedCountries = DEFAULT_COUNTRY_OPTIONS,
  countryValue = 'US',
  phoneValue,
  onCountrySelect,
  onPhoneChange,
  placeholder,
  withCountryFlag = false,
  onValidateError,
  validate = false,
  hasError = false,
  ...props
}: PhoneNumberCountryProps & Pick<InputSelectComboProps, 'width'>) => {
  const [searchText, setSearchText] = useState<string | undefined>();
  const [numberHasError, setNumberHasError] = useState(false);

  const selectedCountry = useMemo(
    () => COUNTRY_CALLING_CODES.find((country) => country.countryCode === countryValue),
    [countryValue],
  );
  const options = useMemo(
    () =>
      getCountryOptions(
        COUNTRY_CALLING_CODES,
        supportedCountries,
        searchText?.toLowerCase(),
        withCountryFlag,
      ),
    [searchText, supportedCountries, withCountryFlag],
  );

  const handleSelect = ([selected]: string[]) => {
    onCountrySelect(selected);
  };

  const handleBlur = () => {
    if (validate) {
      const valid = validateNumber(phoneValue, countryValue, onValidateError);
      setNumberHasError(!valid);
    }
  };

  const handleChange = (updatedNumber?: string) => {
    if (
      validatePhoneNumberLength(
        updatedNumber?.replace(/\D/g, '') ?? '',
        countryValue as CountryCode,
      ) !== 'TOO_LONG'
    ) {
      const formatted = new AsYouType(countryValue as CountryCode).input(updatedNumber ?? '');
      onPhoneChange(formatted ?? updatedNumber ?? '');
    }
  };

  return (
    <InputSelectCombo
      id={`phone-number-country-input-${id}`}
      type="tel"
      autoStripPrefix
      onSelect={handleSelect}
      onChange={handleChange}
      onSearchChange={setSearchText}
      onBlur={handleBlur}
      inputValue={phoneValue}
      selectValue={countryValue}
      prefixString={`+${selectedCountry?.callingCode}`}
      options={options}
      placeholder={placeholder}
      hasError={numberHasError || hasError}
      SelectTrigger={PhoneSelectTrigger(withCountryFlag)}
      width="220px"
      {...props}
    />
  );
};
